import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Receipt, CreditCard, Building2, Heart, GraduationCap, Baby, Home, Briefcase, Info, Plus, Trash2 } from 'lucide-react';
import { TAX_DEDUCTIONS, TAX_CREDITS, RETIREMENT_ACCOUNTS } from '../constants';
import { motion } from 'framer-motion';

interface RetirementContribution {
  type: keyof typeof RETIREMENT_ACCOUNTS;
  amount: number;
}

interface TaxDeductionsProps {
  income: number;
  filingStatus: 'single' | 'joint' | 'head';
  onUpdateTaxSavings: (savings: number) => void;
}

export const TaxDeductions: React.FC<TaxDeductionsProps> = ({
  income,
  filingStatus,
  onUpdateTaxSavings
}) => {
  const [deductions, setDeductions] = useState({
    useStandard: true,
    mortgage: 0,
    charity: 0,
    medical: 0,
    state: 0,
    student: 0
  });

  const [credits, setCredits] = useState({
    children: 0,
    childcare: 0,
    education: 'none',
    educationAmount: 0,
    earned: false,
    homebuyer: false
  });

  const [retirementContributions, setRetirementContributions] = useState<RetirementContribution[]>([]);
  const [age, setAge] = useState<number>(30);

  const addRetirementContribution = () => {
    setRetirementContributions([
      ...retirementContributions,
      { type: 'traditional401k', amount: 0 }
    ]);
  };

  const updateRetirementContribution = (index: number, updates: Partial<RetirementContribution>) => {
    const newContributions = [...retirementContributions];
    newContributions[index] = { ...newContributions[index], ...updates };
    setRetirementContributions(newContributions);
  };

  const removeRetirementContribution = (index: number) => {
    setRetirementContributions(retirementContributions.filter((_, i) => i !== index));
  };

  // Calculate total deductions
  const calculateDeductions = () => {
    if (deductions.useStandard) {
      return TAX_DEDUCTIONS.standard[filingStatus];
    }

    const itemizedTotal = 
      Math.min(deductions.mortgage, TAX_DEDUCTIONS.itemized.mortgage.max) +
      deductions.charity +
      Math.max(0, deductions.medical - (income * TAX_DEDUCTIONS.itemized.medical.threshold)) +
      Math.min(deductions.state, TAX_DEDUCTIONS.itemized.state.max) +
      Math.min(deductions.student, TAX_DEDUCTIONS.itemized.student.max);

    return Math.max(itemizedTotal, TAX_DEDUCTIONS.standard[filingStatus]);
  };

  // Calculate retirement deductions
  const calculateRetirementDeductions = () => {
    return retirementContributions.reduce((total, contribution) => {
      const account = RETIREMENT_ACCOUNTS[contribution.type];
      const annualLimit = age >= (account.catchupAge || 0) 
        ? account.limit + (account.catchup || 0)
        : account.limit;
      
      const annualContribution = (contribution.amount / 100) * income;
      return total + Math.min(annualContribution, annualLimit);
    }, 0);
  };

  // Calculate total credits
  const calculateCredits = () => {
    let totalCredits = 0;

    // Child Tax Credit
    if (income <= TAX_CREDITS.child.income_limit[filingStatus]) {
      totalCredits += credits.children * TAX_CREDITS.child.amount;
    }

    // Child and Dependent Care Credit
    if (credits.childcare > 0) {
      totalCredits += Math.min(
        credits.childcare * TAX_CREDITS.childcare.percentage,
        TAX_CREDITS.childcare.max
      );
    }

    // Education Credits
    if (credits.education === 'aotc' && income <= TAX_CREDITS.education.aotc.income_limit[filingStatus]) {
      totalCredits += Math.min(credits.educationAmount, TAX_CREDITS.education.aotc.max);
    } else if (credits.education === 'llc' && income <= TAX_CREDITS.education.llc.income_limit[filingStatus]) {
      totalCredits += Math.min(credits.educationAmount, TAX_CREDITS.education.llc.max);
    }

    // Earned Income Tax Credit
    if (credits.earned && income <= 60000) {
      const eitcAmount = Math.min(income * 0.15, 7430);
      totalCredits += eitcAmount;
    }

    // First-time Homebuyer Credit
    if (credits.homebuyer) {
      totalCredits += 15000;
    }

    return totalCredits;
  };

  // Calculate tax savings
  const calculateTaxSavings = () => {
    const totalDeductions = calculateDeductions();
    const retirementDeductions = calculateRetirementDeductions();
    const totalCredits = calculateCredits();
    
    // Estimate tax bracket (simplified)
    const estimatedTaxRate = income > 100000 ? 0.24 : income > 50000 ? 0.22 : 0.12;
    
    // Calculate deduction savings
    const deductionSavings = (totalDeductions + retirementDeductions) * estimatedTaxRate;
    
    // Total savings (deductions + credits)
    return deductionSavings + totalCredits;
  };

  // Update parent component when savings change
  useEffect(() => {
    const totalSavings = calculateTaxSavings();
    onUpdateTaxSavings(totalSavings);
  }, [deductions, credits, retirementContributions, income, filingStatus]);

  return (
    <div className="space-y-6">
      {/* Standard vs Itemized Deductions */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={deductions.useStandard}
              onChange={(e) => setDeductions({ ...deductions, useStandard: true })}
              className="text-emerald-500"
            />
            <span>Standard Deduction (${TAX_DEDUCTIONS.standard[filingStatus].toLocaleString()})</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!deductions.useStandard}
              onChange={(e) => setDeductions({ ...deductions, useStandard: false })}
              className="text-emerald-500"
            />
            <span>Itemized Deductions</span>
          </label>
        </div>

        {!deductions.useStandard && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid md:grid-cols-2 gap-4"
          >
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Mortgage Interest</label>
              <div className="relative">
                <Building2 className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={deductions.mortgage}
                  onChange={(e) => setDeductions({ ...deductions, mortgage: Number(e.target.value) })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Charitable Contributions</label>
              <div className="relative">
                <Heart className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={deductions.charity}
                  onChange={(e) => setDeductions({ ...deductions, charity: Number(e.target.value) })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Medical Expenses</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={deductions.medical}
                  onChange={(e) => setDeductions({ ...deductions, medical: Number(e.target.value) })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">State & Local Taxes</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={deductions.state}
                  onChange={(e) => setDeductions({ ...deductions, state: Number(e.target.value) })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Retirement Accounts */}
      <div className="glass-card p-6 border border-emerald-500/20">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-500" />
            Retirement Accounts
          </h3>
          <button
            onClick={addRetirementContribution}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        </div>

        <div className="space-y-2 mb-4">
          <label className="block text-sm text-slate-400">Age</label>
          <input
            type="number"
            value={age}
            onChange={(e) => setAge(Number(e.target.value))}
            className="glass-input px-4 py-2 w-full rounded-lg"
            min="0"
            max="100"
          />
        </div>

        <div className="space-y-4">
          {retirementContributions.map((contribution, index) => {
            const account = RETIREMENT_ACCOUNTS[contribution.type];
            const annualLimit = age >= (account.catchupAge || 0)
              ? account.limit + (account.catchup || 0)
              : account.limit;
            
            const annualContribution = (contribution.amount / 100) * income;
            const isOverLimit = annualContribution > annualLimit;
            
            return (
              <div key={index} className="glass-card p-4 border border-emerald-500/20">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm text-slate-400">Account Type</label>
                    <select
                      value={contribution.type}
                      onChange={(e) => updateRetirementContribution(index, { 
                        type: e.target.value as keyof typeof RETIREMENT_ACCOUNTS 
                      })}
                      className="glass-input px-4 py-2 w-full rounded-lg"
                    >
                      {Object.entries(RETIREMENT_ACCOUNTS).map(([key, account]) => (
                        <option key={key} value={key}>
                          {account.label} (Limit: ${annualLimit.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm text-slate-400">Contribution (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={contribution.amount}
                        onChange={(e) => updateRetirementContribution(index, { 
                          amount: Number(e.target.value)
                        })}
                        className={`glass-input px-4 py-2 w-full rounded-lg ${
                          isOverLimit ? 'border-red-500' : ''
                        }`}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-sm">
                  <p className="text-slate-400">{RETIREMENT_ACCOUNTS[contribution.type].description}</p>
                  {RETIREMENT_ACCOUNTS[contribution.type].incomeLimit && (
                    <p className="mt-2 text-slate-400">
                      Income limits: ${RETIREMENT_ACCOUNTS[contribution.type].incomeLimit.single.toLocaleString()} (Single) / 
                      ${RETIREMENT_ACCOUNTS[contribution.type].incomeLimit.joint.toLocaleString()} (Joint)
                    </p>
                  )}
                  <p className="mt-2">
                    Annual contribution: ${annualContribution.toLocaleString()}
                    {isOverLimit && (
                      <span className="text-red-500 ml-2">
                        (Exceeds ${annualLimit.toLocaleString()} limit)
                      </span>
                    )}
                  </p>
                </div>

                <button
                  onClick={() => removeRetirementContribution(index)}
                  className="mt-4 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tax Credits */}
      <div className="glass-card p-6 border border-emerald-500/20">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-emerald-500" />
          Tax Credits
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-400">Child Tax Credit</label>
            <div className="relative">
              <Baby className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="number"
                value={credits.children}
                onChange={(e) => setCredits({ ...credits, children: Number(e.target.value) })}
                className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                min="0"
                placeholder="Number of children"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm text-slate-400">Child Care Expenses</label>
            <div className="relative">
              <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="number"
                value={credits.childcare}
                onChange={(e) => setCredits({ ...credits, childcare: Number(e.target.value) })}
                className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                min="0"
                placeholder="Annual expenses"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm text-slate-400">Education Credit</label>
            <select
              value={credits.education}
              onChange={(e) => setCredits({ ...credits, education: e.target.value })}
              className="glass-input px-4 py-2 w-full rounded-lg"
            >
              <option value="none">None</option>
              <option value="aotc">American Opportunity (AOTC)</option>
              <option value="llc">Lifetime Learning Credit</option>
            </select>
          </div>

          {credits.education !== 'none' && (
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Education Expenses</label>
              <div className="relative">
                <GraduationCap className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={credits.educationAmount}
                  onChange={(e) => setCredits({ ...credits, educationAmount: Number(e.target.value) })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  placeholder="Annual expenses"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={credits.earned}
                onChange={(e) => setCredits({ ...credits, earned: e.target.checked })}
                className="text-emerald-500"
              />
              <span className="text-sm text-slate-400">Earned Income Tax Credit (EITC)</span>
            </label>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={credits.homebuyer}
                onChange={(e) => setCredits({ ...credits, homebuyer: e.target.checked })}
                className="text-emerald-500"
              />
              <span className="text-sm text-slate-400">First-time Homebuyer Credit</span>
            </label>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="glass-card p-6 border border-emerald-500/20">
        <h3 className="text-lg font-semibold mb-4">Tax Savings Summary</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-slate-400 mb-1">Deductions Value</p>
            <p className="text-2xl font-bold text-emerald-500">
              ${calculateDeductions().toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Retirement Deductions</p>
            <p className="text-2xl font-bold text-emerald-500">
              ${calculateRetirementDeductions().toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Tax Credits</p>
            <p className="text-2xl font-bold text-emerald-500">
              ${calculateCredits().toLocaleString()}
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-emerald-500/20">
          <p className="text-sm text-slate-400 mb-1">Estimated Total Tax Savings</p>
          <p className="text-3xl font-bold text-emerald-500">
            ${calculateTaxSavings().toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};